import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { Bar } from 'react-chartjs-2';
import { ChartBarIcon, ClipboardDocumentCheckIcon, ClockIcon, PlayIcon } from '@heroicons/react/24/outline';
import logger from '../../utils/logger';

const TaskDashboard = () => {
  const [taskStats, setTaskStats] = useState({ new: 0, 'in-progress': 0, completed: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'tasks'));
        const tasksData = querySnapshot.docs.map(doc => doc.data());

        const stats = tasksData.reduce(
          (acc, task) => {
            const status = task.status || 'new';
            if (status in acc) {
              acc[status] += 1;
            }
            return acc;
          },
          { new: 0, 'in-progress': 0, completed: 0 }
        );

        setTaskStats(stats);
      } catch (error) {
        logger.error('Error fetching tasks:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, []);

  const data = {
    labels: ['New', 'In Progress', 'Completed'],
    datasets: [
      {
        label: 'Tasks',
        data: [taskStats.new, taskStats['in-progress'], taskStats.completed],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(251, 191, 36, 0.8)',
          'rgba(34, 197, 94, 0.8)'
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(251, 191, 36)',
          'rgb(34, 197, 94)'
        ],
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
    },
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-500 via-pink-500 to-pink-600 py-4 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-4 text-center">
          <div className="flex items-center justify-center mb-2">
            <ChartBarIcon className="h-10 w-10 text-white mr-3" />
            <h1 className="text-3xl font-black text-white">Task Dashboard</h1>
          </div>
          <p className="text-purple-50 text-sm">Overview of task status and metrics</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* New Tasks */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-xs font-semibold uppercase">New Tasks</p>
                <p className="text-4xl font-black mt-1">{taskStats.new}</p>
              </div>
              <ClipboardDocumentCheckIcon className="h-14 w-14 text-blue-200 opacity-80" />
            </div>
          </div>

          {/* In Progress */}
          <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl shadow-lg p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-xs font-semibold uppercase">In Progress</p>
                <p className="text-4xl font-black mt-1">{taskStats['in-progress']}</p>
              </div>
              <PlayIcon className="h-14 w-14 text-amber-200 opacity-80" />
            </div>
          </div>

          {/* Completed */}
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-xs font-semibold uppercase">Completed</p>
                <p className="text-4xl font-black mt-1">{taskStats.completed}</p>
              </div>
              <ClockIcon className="h-14 w-14 text-green-200 opacity-80" />
            </div>
          </div>
        </div>

        {/* Chart Card */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Task Distribution</h2>
          <div style={{ height: '400px' }}>
            <Bar data={data} options={options} />
          </div>
        </div>

        {/* Summary Stats */}
        <div className="bg-white rounded-xl shadow-lg p-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-black text-gray-800">
                {taskStats.new + taskStats['in-progress'] + taskStats.completed}
              </p>
              <p className="text-xs font-semibold text-gray-600 uppercase">Total Tasks</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-black text-gray-800">
                {taskStats.completed > 0
                  ? Math.round((taskStats.completed / (taskStats.new + taskStats['in-progress'] + taskStats.completed)) * 100)
                  : 0}%
              </p>
              <p className="text-xs font-semibold text-gray-600 uppercase">Completion Rate</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-black text-gray-800">
                {taskStats.new + taskStats['in-progress']}
              </p>
              <p className="text-xs font-semibold text-gray-600 uppercase">Active Tasks</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDashboard;
